import Head from 'next/head'
import Image, { StaticImageData } from 'next/image'

import Badge from '@ilg-totallymoney/ui/components/Badge'
import Box from '@ilg-totallymoney/ui/components/Box'
import Heading from '@ilg-totallymoney/ui/components/Heading'
import LinkWithChevron from '@ilg-totallymoney/ui/components/LinkWithChevron'
import Stack from '@ilg-totallymoney/ui/components/Stack'
import Text from '@ilg-totallymoney/ui/components/Text'

import useAppQueryState from '@hooks/useAppQueryState'

import peachCard from '@images/related-guides-peach.png'
import pinkCard from '@images/related-guides-pink.png'
import yellowCard from '@images/related-guides-yellow.png'

import { StyledCard } from './RelatedArticleSection.styled'
import { relatedArticles } from './lib/relatedArticles'

const colorVariants: Record<number, { alt: string; src: StaticImageData }> = {
    0: {
        alt: 'TotallyMoney guide pink',
        src: pinkCard,
    },

    1: {
        alt: 'TotallyMoney guide yellow',
        src: yellowCard,
    },

    2: {
        alt: 'TotallyMoney guide peach',
        src: peachCard,
    },
}

const RelatedArticleSection = () => {
    const { appQueryState } = useAppQueryState()
    const category = appQueryState.categoryId!

    const relatedArticlesData = relatedArticles[category]

    return (
        <>
            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'ItemList',
                            name: 'Related Articles',
                            itemListElement: relatedArticlesData.map(
                                ({ href, title }, i) => ({
                                    '@type': 'ListItem',
                                    position: i + 1,
                                    item: {
                                        '@type': 'Article',
                                        headline: title,
                                        url: href,
                                    },
                                })
                            ),
                        }),
                    }}
                />
            </Head>

            <Box gridColumn="1/13">
                <Stack
                    space="space20"
                    direction={['column', 'row']}
                    justifyContent="space-between"
                    alignItems={['flex-start', 'center']}
                    mb="space30"
                >
                    <Heading variant="100">Related articles</Heading>

                    <LinkWithChevron href="https://www.totallymoney.com/credit-cards/guides/">
                        See more guides
                    </LinkWithChevron>
                </Stack>

                <Box
                    display="grid"
                    gridTemplateColumns={['1fr', '1fr', 'repeat(3, 1fr)']}
                    gridGap="space30"
                >
                    {relatedArticlesData.map(
                        ({ href, subheading, title }, i: number) => (
                            <StyledCard
                                as="a"
                                href={href}
                                p="space30"
                                pt={0}
                                key={`${title}-${i}`}
                                width="100%"
                                style={{ textDecoration: 'none' }}
                                overflow="hidden"
                            >
                                <Box
                                    position="relative"
                                    mx="-space30"
                                    height="216px"
                                    mb="space30"
                                >
                                    <Image
                                        src={colorVariants[i].src}
                                        alt={colorVariants[i].alt}
                                        placeholder="empty"
                                        sizes="(max-width: 768px) 100vw, 33vw"
                                        quality={60}
                                        priority={false}
                                        fill
                                        style={{
                                            objectFit: 'cover',
                                        }}
                                    />
                                </Box>

                                <Stack space="space10">
                                    <Box>
                                        <Badge
                                            text="Credit cards"
                                            size="medium"
                                        />
                                    </Box>

                                    <Heading variant="60">{title}</Heading>

                                    <Text variant="120" color="light">
                                        {subheading}
                                    </Text>
                                </Stack>
                            </StyledCard>
                        )
                    )}
                </Box>
            </Box>
        </>
    )
}

export default RelatedArticleSection
